import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Copy, MessageCircle, X } from "lucide-react";
import { Button } from "@v2/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@v2/components/ui/dialog";
import { copyToClipboard, openWhatsApp } from "@v2/lib/share";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  text: string;
};

export function ShareTextDialog({ open, onOpenChange, title, text }: Props) {
  const { t } = useTranslation("common");
  const [copied, setCopied] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  const handleCopy = async () => {
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } else {
      textareaRef.current?.focus();
      textareaRef.current?.select();
    }
  };

  const handleWhatsApp = () => {
    openWhatsApp(text);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[17px]">{title}</DialogTitle>
          <DialogDescription className="text-[13px]">
            {t("common.sharePreview")}
          </DialogDescription>
        </DialogHeader>
        <textarea
          ref={textareaRef}
          readOnly
          value={text}
          rows={8}
          className="w-full text-sm rounded-[14px] bg-raised p-3 leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-ring"
          onFocus={(e) => e.currentTarget.select()}
        />
        <div className="flex flex-wrap gap-2 justify-end">
          <Button variant="outline" onClick={handleCopy} className="gap-2">
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? t("common.copied") : t("common.copyText")}
          </Button>
          <Button variant="default" onClick={handleWhatsApp} className="gap-2 bg-[#25D366] hover:bg-[#25D366]/90 text-[#0a1114]">
            <MessageCircle className="h-4 w-4" />
            {t("common.openWhatsApp")}
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="gap-2">
            <X className="h-4 w-4" />
            {t("common.close")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
